import React, { useEffect, useRef, useState } from 'react';

const TurnstileWidget = ({
  enabled,
  siteKey,
  onVerify,
  onExpire,
  onError,
}) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const callbacksRef = useRef({ onVerify, onExpire, onError });
  const [isReady, setIsReady] = useState(Boolean(window.turnstile));

  useEffect(() => {
    callbacksRef.current = { onVerify, onExpire, onError };
  }, [onVerify, onExpire, onError]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    if (window.turnstile) {
      setIsReady(true);
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      if (window.turnstile) {
        setIsReady(true);
        window.clearInterval(intervalId);
      }
    }, 100);

    return () => window.clearInterval(intervalId);
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !siteKey || !isReady || !containerRef.current || widgetIdRef.current !== null) {
      return undefined;
    }

    widgetIdRef.current = window.turnstile.render(containerRef.current, {
      sitekey: siteKey,
      callback: (token) => callbacksRef.current.onVerify?.(token),
      'expired-callback': () => callbacksRef.current.onExpire?.(),
      'error-callback': () => callbacksRef.current.onError?.(),
      theme: 'auto',
    });

    return () => {
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [enabled, isReady, siteKey]);

  if (!enabled) {
    return null;
  }

  if (!siteKey) {
    return (
      <div className="rounded-lg border border-red-400/40 bg-red-500/10 px-4 py-3 text-sm text-red-100">
        Security verification is not available right now. Please try again later.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {!isReady && (
        <p className="text-sm text-white/70">Loading security verification...</p>
      )}
      <div ref={containerRef} data-testid="turnstile-widget" />
    </div>
  );
};

export default TurnstileWidget;
